// starColumns — the explicit select list a bare `SELECT *` stands for: the
// columns the engine's own `*` returns, in the same number and order. The VQE's
// "Choose columns" writes it in place of the `*`.
//
// Built from the statement's FROM items and joins (kind, NATURAL, USING list)
// and each base table's snapshot columns as a left-associative fold over each
// FROM item's join steps: the left side is the columns so far (each with the
// SQL that reads its value), the right side one table. A plain join appends
// the right table's columns; a USING / NATURAL join merges each shared column
// into ONE output column. How the engines place and fill it — probed live on
// 2026-09-27 (PostgreSQL 16, MySQL 8.0 + MariaDB 10.2, Oracle 26ai, SQLite
// 3.51) with t1(a, x, b) / t2(c, x, d) / t3(x, e) and friends:
//
//   `*` order      PostgreSQL, Oracle: the merged columns first (USING-list
//                  order; NATURAL: the left side's order), then the rest of the
//                  left side, then the rest of the right — `x, a, b, c, d`.
//                  MySQL: merged first in the LEFT side's order, then left,
//                  then right; a RIGHT JOIN swaps the sides (merged in the
//                  right table's order, then right, then left — `x, c, d, a, b`).
//                  SQLite: the left side as it is, each merged column where the
//                  left one was, then the rest of the right — `a, x, b, c, d`.
//   comma          PostgreSQL / MySQL / Oracle: JOIN binds tighter, so
//                  `t1, t2 JOIN t3 USING (x)` is t1 × (t2 ⋈ t3). SQLite: a comma
//                  is one more join step, and a name the left side has twice
//                  merges with the leftmost (an error elsewhere: not expanded).
//   merged value   INNER / LEFT: the left column; RIGHT: the right column;
//                  FULL: COALESCE(left, right) — the four engines agree.
//   bare `x`       reads the merged column, unless another output column is
//                  named x too: ambiguous on every engine.
//   `t1.x`         accepted by PostgreSQL, MySQL and SQLite (the column itself);
//                  Oracle rejects any qualifier on it (ORA-25154 / ORA-25155).
//                  SQLite's own `*` fails when a RIGHT / FULL JOIN's merged name
//                  is ambiguous ("ambiguous column name: x"); the list is still
//                  written, reading the value that join defines.
//   NATURAL        with no shared name: a cross join (every engine).
//   SQL Server     has neither USING nor NATURAL — nothing is merged.
//   MySQL FULL     is not a join kind: `t1 FULL JOIN t2` makes FULL t1's alias.
//
// A merged column is written ONCE: bare when its name is unambiguous in the
// output, else as its value's source — `t1.x`, `t2.x` (RIGHT), or
// `COALESCE(t1.x, t2.x) AS x` (FULL). Oracle forbids that qualifier, so there
// the list cannot be written (`unqualifiable`). Every other column is its
// table's qualifier + name. A source whose columns are not known (CTE, derived
// table, table function, a table the snapshot lacks), a USING column a side
// lacks, or a MySQL FULL JOIN makes the whole list `unknown`.
//
// `starColumnList` is the same fold without the writing step: the columns
// themselves (name, data type, the table each is read from, the tables a
// merged column stands for), for surfaces that show the list rather than write
// it — the SQL editor's hover over `*`. It lists an Oracle merged name that
// cannot be written (`unqualifiable` above) like any other.

import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';
import { catalogIdentSql } from '../suggest/sqlIdentifierText';
import type { FromItem, JoinClause, QueryStructure } from '../types/queryStructure';
import type { SchemaSnapshot } from '../types/schema';
import { foldIdentifier, readIdentifier } from './identifiers';
import { bindingKeyOf, querySourceOf, sourceColumns } from './querySources';

/** A FROM source a `*` column comes from: what the query calls it (alias, else table name) and its table name, as typed. */
export type StarColumnSource = { readonly name: string; readonly table: string };

/** One output column of `*` as the engine returns it (`starColumnList`). */
export type StarListColumn = {
  /** The catalog name. */
  readonly name: string;
  readonly dataType: string;
  /** The table whose column holds the value (a merged column: the left one, the right one on a RIGHT JOIN). */
  readonly source: StarColumnSource;
  /** A USING / NATURAL merge: every table it stands for, in join order. */
  readonly sharedBy?: readonly StarColumnSource[];
};

/** One output column of `*`: its catalog name, the key the engine compares names by, and the SQL reading its value. */
type StarColumn = StarListColumn & {
  readonly key: string;
  readonly sql: string;
  /** Output of a USING / NATURAL merge; `coalesce` when `sql` is COALESCE(left, right). */
  readonly merged?: 'ref' | 'coalesce';
};

export type StarExpansion =
  | { readonly kind: 'columns'; readonly items: readonly string[] }
  | { readonly kind: 'unknown' }
  | { readonly kind: 'unqualifiable' }
  /** MySQL has no FULL JOIN: `a FULL JOIN b` makes FULL `a`'s alias there. */
  | { readonly kind: 'noFullJoin' };

export type StarColumnList =
  | { readonly kind: 'columns'; readonly columns: readonly StarListColumn[] }
  | { readonly kind: 'unknown' }
  | { readonly kind: 'noFullJoin' };

type NoList = { readonly kind: 'unknown' } | { readonly kind: 'noFullJoin' };

const UNKNOWN: NoList = { kind: 'unknown' };

export function starColumns(
  structure: QueryStructure | null | undefined,
  options: ParseOptions,
  snapshot: SchemaSnapshot | null | undefined,
): StarExpansion {
  const engine = options.engine;
  const out = outputColumns(structure, options, snapshot);
  if (!Array.isArray(out)) return out;
  const counts = new Map<string, number>();
  for (const c of out) counts.set(c.key, (counts.get(c.key) ?? 0) + 1);
  const items: string[] = [];
  for (const c of out) {
    if (!c.merged) items.push(c.sql);
    else if (counts.get(c.key) === 1) items.push(catalogIdentSql(c.name, engine));
    else if (engine === 'oracle') return { kind: 'unqualifiable' };
    else items.push(c.merged === 'coalesce' ? `${c.sql} AS ${catalogIdentSql(c.name, engine)}` : c.sql);
  }
  return { kind: 'columns', items };
}

/** The columns `*` returns, in the engine's order — `starColumns` without deciding how to write them. */
export function starColumnList(
  structure: QueryStructure | null | undefined,
  options: ParseOptions,
  snapshot: SchemaSnapshot | null | undefined,
): StarColumnList {
  const out = outputColumns(structure, options, snapshot);
  if (!Array.isArray(out)) return out;
  return {
    kind: 'columns',
    columns: out.map(({ name, dataType, source, sharedBy }) => ({ name, dataType, source, ...(sharedBy ? { sharedBy } : {}) })),
  };
}

/** A FROM item's own columns — what `alias.*` returns; null when they are not known. */
export function fromItemStarColumns(
  item: FromItem,
  engine: SqlEngine,
  snapshot: SchemaSnapshot | null | undefined,
): readonly StarListColumn[] | null {
  return tableColumns(item, engine, snapshot)?.map(({ name, dataType, source }) => ({ name, dataType, source })) ?? null;
}

/** The fold over the FROM items and join steps (see the header). */
function outputColumns(
  structure: QueryStructure | null | undefined,
  options: ParseOptions,
  snapshot: SchemaSnapshot | null | undefined,
): StarColumn[] | NoList {
  const engine = options.engine;
  if (engine === 'mysql' && structure?.joins?.some((j) => j.kind === 'full')) return { kind: 'noFullJoin' };
  // FROM items and join steps in text order: a join belongs to the FROM item before it.
  const steps: { item: FromItem; join?: JoinClause }[] = [
    ...(structure?.fromItems ?? []).map((item) => ({ item })),
    ...(structure?.joins ?? []).map((join) => ({ item: join.right, join })),
  ].sort((a, b) => a.item.range.startOffset - b.item.range.startOffset);
  const out: StarColumn[] = [];
  let left: StarColumn[] | null = null;
  for (const { item, join } of steps) {
    const right = tableColumns(item, engine, snapshot);
    if (!right) return UNKNOWN;
    if (join) left = left && joinStep(left, right, join, options);
    else if (left && engine !== 'sqlite') {
      out.push(...left);
      left = right;
    } else left = [...(left ?? []), ...right];
    if (!left) return UNKNOWN;
  }
  if (!left) return UNKNOWN;
  out.push(...left);
  return out;
}

/** A base table's columns under its qualifier; null when they are not known. */
function tableColumns(item: FromItem, engine: SqlEngine, snapshot: SchemaSnapshot | null | undefined): StarColumn[] | null {
  const source = querySourceOf(item, engine);
  const columns = source.kind === 'relation' ? sourceColumns(source, snapshot) : [];
  if (columns.length === 0) return null;
  const from: StarColumnSource = {
    name: source.exposedName,
    table: item.target.kind === 'relation' ? item.target.qualified.name : source.exposedName,
  };
  return columns.map(({ qualified: { name }, dataType }) => ({
    name,
    dataType,
    source: from,
    key: bindingKeyOf(engine, name),
    sql: `${source.qualifierSql}.${catalogIdentSql(name, engine)}`,
  }));
}

function joinStep(left: StarColumn[], right: StarColumn[], join: JoinClause, options: ParseOptions): StarColumn[] | null {
  const engine = options.engine;
  const keys = sharedKeys(left, right, join, options);
  if (!keys) return null;
  const pairs: [StarColumn, StarColumn][] = [];
  for (const key of keys) {
    const l = left.filter((c) => c.key === key);
    const r = right.find((c) => c.key === key);
    if (!r || l.length === 0 || (l.length > 1 && engine !== 'sqlite')) return null;
    pairs.push([l[0]!, r]);
  }
  const merge = ([l, r]: [StarColumn, StarColumn]): StarColumn => {
    const sharedBy = [...(l.sharedBy ?? [l.source]), ...(r.sharedBy ?? [r.source])];
    if (join.kind === 'full') return { ...l, sql: `COALESCE(${l.sql}, ${r.sql})`, merged: 'coalesce', sharedBy };
    const kept = join.kind === 'right' ? r : l;
    return { ...kept, merged: kept.merged ?? 'ref', sharedBy };
  };
  const restLeft = left.filter((c) => !pairs.some(([l]) => l === c));
  const restRight = right.filter((c) => !pairs.some(([, r]) => r === c));
  if (engine === 'sqlite') {
    return [...left.map((c) => { const p = pairs.find(([l]) => l === c); return p ? merge(p) : c; }), ...restRight];
  }
  if (engine !== 'mysql') return [...pairs.map(merge), ...restLeft, ...restRight];
  const swap = join.kind === 'right';
  const side = swap ? right : left;
  const ordered = [...pairs].sort((p, q) => side.indexOf(p[swap ? 1 : 0]) - side.indexOf(q[swap ? 1 : 0])).map(merge);
  return swap ? [...ordered, ...restRight, ...restLeft] : [...ordered, ...restLeft, ...restRight];
}

/** The keys a USING / NATURAL join merges, in USING-list (NATURAL: left side) order; null when unreadable. */
function sharedKeys(left: StarColumn[], right: StarColumn[], join: JoinClause, options: ParseOptions): string[] | null {
  if (options.engine === 'mssql') return [];
  if (join.using) {
    const keys: string[] = [];
    for (const text of join.using) {
      const ident = readIdentifier(text, 0, text.length, options);
      if (!ident || ident.end !== text.length) return null;
      keys.push(bindingKeyOf(options.engine, foldIdentifier(ident.value, ident.quoted, options)));
    }
    return new Set(keys).size === keys.length ? keys : null;
  }
  if (!join.natural) return [];
  const rightKeys = new Set(right.map((c) => c.key));
  return [...new Set(left.map((c) => c.key))].filter((key) => rightKeys.has(key));
}
